import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const getVendorName = (vendor) => {
  if (!vendor) return "—";
  if (vendor.company_name) return vendor.company_name;
  const u = vendor.primary_users;
  return u ? `${u.first_name} ${u.last_name}`.trim() : "—";
};

const COVERAGE_BADGE = {
  "Fully covered":    "text-green-600 font-medium",
  "Not covered":      "text-danger-500 font-medium",
  "Partially covered":"text-amber-600 font-medium",
};

const AdminVendorInvoicesPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Project managers may only view vendor invoices - creating payments from
  // them is rejected server-side, so the write controls (and the row selection
  // that only feeds them) are hidden.
  const readOnly = isReadOnly(user, MODULES.VENDOR_INVOICES);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/project-finances/vendor-invoices", { withCredentials: true });
        setInvoices(res.data.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch vendor invoices");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getCoverage = (inv) => {
    const due = Number(inv.due_amount || 0);
    const paid = Number(inv.paid_amount || 0);
    const total = Number(inv.total || 0);
    if (total === 0) return "Not covered";
    if (due <= 0) return "Fully covered";
    if (paid > 0) return "Partially covered";
    return "Not covered";
  };

  const coverByPayments = async (ids) => {
    if (ids.length === 0) return;
    setPaymentLoading(true);
    try {
      const { data } = await api.post(
        "/project-finances/vendor-payments",
        { invoiceIds: ids },
        { withCredentials: true }
      );
      if (data.success) {
        setSelectedIds([]);
        const created = data.data || [];
        if (created.length === 1) {
          sessionStorage.setItem("paymentSuccessMsg", "1 payment has been created.");
          navigate(`/vendor/payment/${created[0].id}`);
        } else {
          sessionStorage.setItem("paymentSuccessMsg", `${created.length} payment(s) have been created.`);
          navigate("/vendor/payments");
        }
      }
    } catch (err) {
      console.error("Cover by payments failed:", err);
      toast.error("Failed to create payment(s).");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleToggle = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const allSelected = invoices.length > 0 && invoices.every((inv) => selectedIds.includes(inv.id));
  const someSelected = invoices.some((inv) => selectedIds.includes(inv.id));

  const HEADERS = [
    "Code", "Linked payments", "Vendor", "Payment coverage", "Total", "Write off amount", "Paid amount",
    "Due amount", "Currency", "Issued at", "Due at",
  ];

  // Mirrors HEADERS, in the same order, minus the checkbox and action columns.
  const EXPORT_COLUMNS = [
    { header: "Code", value: (inv) => inv.invoice_code || "" },
    {
      header: "Linked payments",
      value: (inv) => inv.paymentMethod?.payment_method?.replace(/_/g, " ") || "",
    },
    { header: "Vendor", value: (inv) => getVendorName(inv.vendor) },
    { header: "Payment coverage", value: (inv) => getCoverage(inv) },
    { header: "Total", value: (inv) => fmtNum(inv.total) },
    { header: "Write off amount", value: (inv) => fmtNum(inv.write_off_amount) },
    { header: "Paid amount", value: (inv) => fmtNum(inv.paid_amount) },
    { header: "Due amount", value: (inv) => fmtNum(inv.due_amount) },
    { header: "Currency", value: (inv) => inv.currency?.currency?.code || "" },
    { header: "Issued at", value: (inv) => fmtDate(inv.issued_at) },
    { header: "Due at", value: (inv) => fmtDate(inv.due_at) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendor Invoices</h1>
        <div className="flex items-center gap-2 text-sm">
          {!readOnly && (
            <>
              <span className="text-xs text-gray-500">
                New vendor invoices can be created on the{" "}
                <Link to="/vendor/payables" className="text-brand-600 hover:underline font-medium">
                  Payables
                </Link>{" "}
                page.
              </span>
              <button
                disabled={selectedIds.length === 0 || paymentLoading}
                onClick={() => coverByPayments(selectedIds)}
                className={btn("primary", "sm")}
              >
                {paymentLoading ? "Creating..." : "Cover (selection) by payments"}
              </button>
              <span className="text-gray-300">|</span>
              <button
                disabled={paymentLoading}
                onClick={() => {
                  const eligible = invoices.filter((inv) => Number(inv.due_amount || 0) > 0).map((inv) => inv.id);
                  coverByPayments(eligible);
                }}
                className={btn("primary", "sm")}
              >
                {paymentLoading ? "Creating..." : "Cover (filter results) by payments"}
              </button>
            </>
          )}
          <button
            onClick={() => exportTable("vendor-invoices", EXPORT_COLUMNS, invoices)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{invoices.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {!readOnly && (
                      <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => {
                            if (allSelected) setSelectedIds([]);
                            else setSelectedIds(invoices.map((i) => i.id));
                          }}
                          className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                        />
                      </th>
                    )}
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold text-center whitespace-nowrap">{h}</th>
                    ))}
                    <th className="px-3 py-2 font-semibold text-center whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={HEADERS.length + (readOnly ? 1 : 2)} className="py-10 text-center text-gray-400 text-xs">
                        No vendor invoices found.
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv, i) => {
                      const coverage = getCoverage(inv);
                      const coverageCls = COVERAGE_BADGE[coverage] ?? "text-gray-600";
                      const currCode = inv.currency?.currency?.code || "—";
                      const isSelected = selectedIds.includes(inv.id);
                      const rowBg = isSelected ? "bg-brand-50" : i % 2 === 0 ? "bg-gray-50" : "bg-white";

                      return (
                        <tr
                          key={inv.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          {!readOnly && (
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggle(inv.id)}
                                className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                              />
                            </td>
                          )}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/invoice/${inv.id}`}
                              className="text-brand-600 hover:text-brand-800 font-medium underline"
                            >
                              {inv.invoice_code}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs capitalize text-center">
                            {inv.paymentMethod?.payment_method?.replace(/_/g, " ") || "—"}
                          </td>
                          
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {inv.vendor_id ? (
                              <Link to={`/vendors/${inv.vendor_id}`} className="text-brand-600 hover:underline font-medium">
                                {getVendorName(inv.vendor)}
                              </Link>
                            ) : getVendorName(inv.vendor)}
                          </td>
                          <td className={`px-3 py-2 text-xs text-center ${coverageCls}`}>{coverage}</td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">{fmtNum(inv.total)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(inv.write_off_amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(inv.paid_amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(inv.due_amount)}</td>
                          <td className="px-3 py-2 text-xs text-center">{currCode}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">{fmtDate(inv.issued_at)}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">{fmtDate(inv.due_at)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              title="Download invoice"
                              className="text-gray-400 hover:text-brand-600 transition-colors"
                              onClick={() => toast.error("Download coming soon")}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVendorInvoicesPage;
